import React, { useState, useMemo } from "react";
import BookCard from "./BookCard";
import { useCart } from "../../hooks/useCart";
import { books as booksData } from "../../data/books";
import { Helmet } from "react-helmet-async"; 

const BooksPage = () => {
  const { addToCart } = useCart();
  const [selectedSubject, setSelectedSubject] = useState("All");
  const [selectedGrade, setSelectedGrade] = useState("All");

  // Dynamically extract unique subjects and grades from booksData
  const { subjects, grades } = useMemo(() => {
    // Get all subjects, filter out duplicates and sort alphabetically
    const uniqueSubjects = [...new Set(booksData.map((book) => book.subject))].sort();
    // Get all grades that are numbers (exclude null/undefined), remove duplicates, sort numerically
    const uniqueGrades = [...new Set(booksData.map((book) => book.grade).filter(g => g != null))].sort((a, b) => a - b);
    return {
      subjects: ["All", ...uniqueSubjects],
      grades: ["All", ...uniqueGrades],
    };
  }, []);

  const filteredBooks = booksData.filter((book) => {
    const subjectMatch =
      selectedSubject === "All" || book.subject === selectedSubject;

    const gradeMatch =
      selectedGrade === "All" || book.grade === Number(selectedGrade);

    return subjectMatch && gradeMatch;
  });

  // Group books by subject (subjects are already sorted alphabetically)
  const groupedBooks = useMemo(() => {
    const groups = {};
    filteredBooks.forEach((book) => {
      const subject = book.subject;
      if (!groups[subject]) groups[subject] = [];
      groups[subject].push(book);
    });
    return groups;
  }, [filteredBooks]);

  // Get sorted list of subjects that have books after filtering
  const subjectsWithBooks = Object.keys(groupedBooks).sort();

  return (
    <>
      <Helmet>
        <title>Assessment Books, SPK Academy</title>
        <meta name="description" content="Buy term-by-term assessment books for Mathematics and Physical Sciences." />
        <meta property="og:title" content="Assessment Books, SPK Academy" />
        <meta property="og:url" content="https://spkrobertacademy.com/books" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="py-6 container">
        <h1 className="text-3xl font-bold mb-6 text-center">
          Term by Term Assessment Books
        </h1>

        {/* FILTER SECTION */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <select
            className="border px-2 py-2 rounded"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            {subjects.map((subj) => (
              <option key={subj} value={subj}>
                {subj}
              </option>
            ))}
          </select>

          <select
            className="border px-2 py-2 rounded"
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
          >
            {grades.map((grade) => (
              <option key={grade} value={grade}>
                {grade}
              </option>
            ))}
          </select>
        </div>

        {/* SECTIONS – dynamically generated and sorted alphabetically */}
        {subjectsWithBooks.map((subject) => (
          <div key={subject} className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">{subject}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {groupedBooks[subject].map((book) => (
                <BookCard key={book.id} book={book} onAddToCart={addToCart} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>

  );
};

export default BooksPage;